// Kandake Internal Alpha — Platform-wide controlled lists.
// Single source of truth for every dropdown that should be consistent across
// Catalog Track, Ready Tracks, License Requests, Documents, Submissions,
// and Settings. Where a list supports "Other", consumers must render an
// explanation/description field when "Other" is selected (use <OtherField/>).

export const OTHER = "Other";

// ---------- Genre (exhaustive, ordered roughly Western → world → niche) ----------
export const GENRES = [
  "Pop", "Rock", "Hip-Hop / Rap", "R&B / Soul", "Funk",
  "Electronic / Dance", "House", "Techno", "Drum & Bass", "Trance",
  "Jazz", "Blues", "Classical", "Orchestral / Cinematic", "Choral",
  "Country", "Folk", "Americana", "Bluegrass",
  "Reggae", "Dancehall", "Latin", "Reggaeton", "Salsa", "Bachata",
  "Afrobeats", "Afro House", "Amapiano", "Afro-Pop", "Highlife", "Soukous",
  "Gospel", "Worship / Faith-Based", "Contemporary Christian", "Spiritual",
  "World", "Caribbean", "Soca", "Ska",
  "Indie", "Alternative", "Punk", "Metal", "Hard Rock",
  "Lo-Fi", "Ambient", "Chillout", "New Age",
  "Spoken Word", "Soundtrack / Score",
  "Children's", "Holiday",
  OTHER,
];

// ---------- Sub-genres (broad, practical) ----------
export const SUB_GENRES = [
  "Trap", "Boom Bap", "Drill", "Conscious Hip-Hop", "Old School Hip-Hop",
  "Neo-Soul", "Contemporary R&B", "Quiet Storm",
  "Synth-Pop", "Dream Pop", "K-Pop", "Latin Pop", "Pop-Punk",
  "Indie Rock", "Garage Rock", "Post-Rock", "Progressive Rock",
  "Deep House", "Tech House", "Tropical House", "EDM Festival", "Future Bass",
  "Smooth Jazz", "Bebop", "Fusion", "Acid Jazz",
  "Roots Reggae", "Lover's Rock",
  "Soft Rock", "Yacht Rock",
  "Trailer / Epic", "Modern Cinematic", "Minimalist Score",
  "Lo-Fi Hip-Hop", "Lo-Fi Beats",
  "Praise & Worship", "Contemporary Gospel", "Traditional Gospel",
  "Naija Pop", "Highlife Modern", "Amapiano Vocal", "Afro-Fusion",
  "Children's Educational", "Lullaby",
  OTHER,
];

// ---------- Mood tags (mirrors Catalog Track Page list — keep authoritative) ----------
// Sourced from existing MOOD_TAGS in constants.js. Re-exported here for clarity.
export { MOOD_TAGS as MOOD_TAGS_REF } from "@/lib/constants";

// ---------- Best Use (sync-use scenarios) ----------
export const BEST_USE_OPTIONS = [
  "Film", "TV", "Commercial / Advertising", "Trailer", "Documentary",
  "Reality TV", "Sports", "Gaming",
  "Social Media / Digital Campaign", "Podcast",
  "Corporate Video", "Brand Campaign",
  "Worship / Faith-Based",
  "Wedding / Romantic",
  "Travel / Lifestyle", "Urban / Street Scene",
  "Emotional Scene", "Celebration / Party",
  "Background / Underscore",
  "Title Sequence", "End Credits",
  OTHER,
];

// ---------- Territory / Market ----------
export const TERRITORY_OPTIONS = [
  "Worldwide",
  "United States", "Canada", "Mexico", "North America",
  "United Kingdom", "European Union", "Europe Non-EU",
  "Caribbean",
  "Africa", "South Africa", "West Africa", "East Africa",
  "Latin America", "South America", "Brazil",
  "Asia", "Japan", "South Korea", "China",
  "Australia / New Zealand",
  "Middle East",
  "Territory Restricted",
  "Rights Unknown / Needs Review",
  OTHER,
];

// ---------- License term length ----------
// MP12 Module 3 §F — "Perpetual" removed; keep only "In perpetuity".
// Do not reintroduce "Perpetual Use".
export const TERM_LENGTH_OPTIONS = [
  "3 months", "6 months", "1 year", "2 years", "3 years", "5 years", "10 years",
  "In perpetuity", "TBD",
  OTHER,
];

// ---------- Budget range ----------
export const BUDGET_RANGE_OPTIONS = [
  "Under $1K", "$1K–$5K", "$5K–$10K", "$10K+", "TBD / To Be Determined",
  OTHER,
];

// ---------- Attorney Review reason ----------
export const ATTORNEY_REVIEW_REASONS = [
  "Ownership conflict",
  "Missing split sheet",
  "Missing LOD / authorization",
  "Sample or interpolation concern",
  "Derivative work concern",
  "Publisher/admin conflict",
  "Territory restriction concern",
  "Chain of title unclear",
  "Contract ambiguity",
  "Master rights issue",
  "Publishing rights issue",
  "Artwork/visual asset concern",
  OTHER,
];

// ---------- Search criteria categories (per module) ----------
export const SEARCH_CATEGORIES = {
  catalog: [
    { id: "all",          label: "Any field" },
    { id: "track_title",  label: "Track title" },
    { id: "artist",       label: "Artist" },
    { id: "writer",       label: "Writer" },
    { id: "publisher",    label: "Publisher" },
    { id: "isrc",         label: "ISRC" },
    { id: "catalog_code", label: "Catalog code" },
    { id: "notes",        label: "Notes" },
  ],
  ready: [
    { id: "all",          label: "Any field" },
    { id: "track_title",  label: "Track title" },
    { id: "artist",       label: "Artist" },
    { id: "writer",       label: "Writer" },
    { id: "publisher",    label: "Publisher" },
    { id: "genre",        label: "Genre" },
    { id: "sub_genre",    label: "Sub-genre" },
    { id: "mood",         label: "Mood" },
    { id: "catalog_code", label: "Catalog code" },
    { id: "best_use",     label: "Best use" },
    { id: "clearance_status", label: "Clearance status" },
    { id: "bpm",          label: "BPM" },
    { id: "duration",     label: "Duration" },
    { id: "isrc",         label: "ISRC" },
    { id: "notes",        label: "Notes" },
  ],
  license: [
    { id: "all",                 label: "Any field" },
    { id: "request_title",       label: "Request title" },
    { id: "requester_name",      label: "Requester / client" },
    { id: "requester_company",   label: "Company" },
    { id: "project_title",       label: "Project name" },
    { id: "project_type",        label: "Project type" },
    { id: "genre_requested",     label: "Genre requested" },
    { id: "mood_requested",      label: "Mood requested" },
    { id: "budget_range",        label: "Budget range" },
    { id: "deadline",            label: "Deadline" },
    { id: "request_status",      label: "Status" },
    { id: "assigned_reviewer",   label: "Assigned reviewer" },
    { id: "requested_track_title", label: "Linked track" },
    { id: "notes",               label: "Notes" },
  ],
  documents: [
    { id: "all",              label: "Any field" },
    { id: "document_name",    label: "Document name" },
    { id: "linked_track_title", label: "Track title" },
    { id: "linked_catalog_id",  label: "Catalog ID" },
    { id: "document_type",      label: "Document type" },
    { id: "linked_artist",      label: "Linked artist" },
    { id: "linked_writer",      label: "Linked writer" },
    { id: "linked_publisher",   label: "Linked publisher" },
    { id: "document_status",    label: "Status" },
    { id: "file_url",           label: "File URL" },
    { id: "notes",              label: "Notes" },
  ],
};

// Convenience for fields that store "Other" + free-text companion.
// e.g. { value, other_value } pattern used across forms.
export function isOther(v) {
  return v === OTHER;
}


// ---------------------------------------------------------------------------
// Phase A Run 2 \u2014 Sensitive-action reason category dictionaries.
// Used by /components/ReasonModal.jsx. Every sensitive surface picks the
// vocabulary that matches its action; the ReasonModal renders it in a
// SearchableSelect and requires the user to pick one plus provide a
// free-text detail of at least 10 characters.
// ---------------------------------------------------------------------------

export const WITHDRAW_REASONS = [
  "Rights Reversion",
  "Ownership Dispute",
  "Estate / Heirship Change",
  "Sample or Interpolation Clearance Failure",
  "Publisher / PRO Registration Correction",
  "Duplicate Record",
  "Data Quality \u2014 Withdraw for Rebuild",
  "Client Request",
  "Legal Instruction",
  "Compliance Review Outcome",
  "Other",
];

export const ARCHIVE_REASONS = [
  "Superseded by Newer Version",
  "Legacy Migration \u2014 Cold Storage",
  "No Longer Actively Managed",
  "Client Request",
  "Compliance Review Outcome",
  "Other",
];

export const CANCEL_REQUEST_REASONS = [
  "Duplicate Request",
  "Requester Withdrew",
  "Insufficient Information",
  "Ineligible / Out of Scope",
  "Superseded by New Request",
  "Client Instruction",
  "Compliance Review Outcome",
  "Other",
];

export const USER_DISABLE_REASONS = [
  "Departure \u2014 Employment Ended",
  "Departure \u2014 Contract Ended",
  "Role Change \u2014 Access No Longer Required",
  "Security Concern",
  "Non-Compliance",
  "Extended Leave",
  "Other",
];

export const ROLE_CHANGE_REASONS = [
  "Promotion",
  "Lateral Reassignment",
  "Reduced Access Required",
  "Onboarding Completion",
  "Security Review Outcome",
  "Compliance Review Outcome",
  "Other",
];

export const OWNERSHIP_CHANGE_REASONS = [
  "New Contract / Agreement",
  "Rights Reversion",
  "Ownership Correction \u2014 Data Fix",
  "Split Renegotiation",
  "Estate / Heirship Update",
  "Publisher / Admin Change",
  "Master Owner Change",
  "Compliance Review Outcome",
  "Other",
];

export const CLEARANCE_CHANGE_REASONS = [
  "Additional Documentation Received",
  "Rights Clearance Verified",
  "Attorney Review Completed",
  "New Sample / Interpolation Discovered",
  "Data Correction",
  "Downgrade \u2014 Missing Information",
  "Compliance Review Outcome",
  "Other",
];

export const SYNC_ELIGIBILITY_BLOCK_REASONS = [
  "Rights Dispute Active",
  "Ownership Uncertainty",
  "Sample / Interpolation Not Cleared",
  "Estate / Heirship Uncertainty",
  "Third-Party Approval Required",
  "Territory Restriction",
  "Client Instruction",
  "Compliance Review Outcome",
  "Other",
];
